import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "../config/axiosInstance";
import { QUERIES } from "../config/apiConfig";
import { useApplication } from "./ApplicationContext";

/*
 * Acompanhamento das consultas assíncronas em nível de aplicação.
 *
 * Precisa viver acima das rotas: se o polling ficasse na tela da consulta, sair
 * dela desmontaria o componente e o usuário só descobriria o desfecho no
 * próximo ciclo do sino (até 60s) ou dando refresh. Aqui o acompanhamento
 * sobrevive à navegação, e a hidratação por /async-queries no carregamento faz
 * ele sobreviver também ao refresh e ao fechar/reabrir a aba.
 */
const AsyncQueryContext = createContext(null);

const POLL_INTERVAL_MS = 8000;
const SETTLED = ["DONE", "ERROR"];

const isPending = (job) => job && !SETTLED.includes(job.jobStatus);

/* O status traz o resultado completo da consulta em `result` — útil para a tela
   de histórico, peso morto aqui. Guardar isso para cada job processado encheria
   a memória à toa, então fica de fora do estado do provider. */
const withoutResult = ({ result, ...job }) => job;

export const AsyncQueryProvider = ({ children }) => {
  const { user } = useApplication();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [jobs, setJobs] = useState({});
  const jobsRef = useRef(jobs);
  // Um job pode ser lido por dois ciclos antes do estado assentar; o set impede
  // que o mesmo desfecho vire dois toasts.
  const announcedRef = useRef(new Set());

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  const upsertJob = useCallback((job) => {
    if (!job?.protocol) return;
    setJobs((previous) => ({
      ...previous,
      [job.protocol]: { ...previous[job.protocol], ...withoutResult(job) },
    }));
  }, []);

  const announce = useCallback(
    (job) => {
      if (announcedRef.current.has(job.protocol)) return;
      announcedRef.current.add(job.protocol);

      // O sino recarrega sozinho a cada 60s; forçamos agora para o aviso e o
      // contador de não lidas aparecerem junto com o toast.
      queryClient.invalidateQueries(["system-notifications"]);

      const label = job.queryName ? `“${job.queryName}”` : "Sua consulta";

      if (job.jobStatus === "DONE") {
        toast.success(`${label} ficou pronta. Clique para ver o resultado.`, {
          autoClose: 8000,
          onClick: () =>
            navigate(
              `/reports/queries?protocol=${encodeURIComponent(job.protocol)}`,
            ),
        });
      } else {
        toast.error(
          job.errorMessage || `${label} não pôde ser concluída. Tente de novo.`,
          { autoClose: 8000 },
        );
      }
    },
    [navigate, queryClient],
  );

  /* Hidratação: recupera o que ficou processando de sessões/páginas anteriores.
     Jobs que terminaram enquanto a aba estava fechada não vêm aqui (a rota só
     lista os ativos) — para esses, o aviso já está no sino. */
  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    (async () => {
      try {
        const { data: response } = await axios.get(
          QUERIES.ASYNC_QUERIES_ACTIVE,
        );
        if (cancelled) return;
        (response?.data || []).forEach(upsertJob);
      } catch (error) {
        console.error("Falha ao recuperar consultas em processamento:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, upsertJob]);

  const hasPending = useMemo(() => Object.values(jobs).some(isPending), [jobs]);

  useEffect(() => {
    if (!hasPending) return undefined;

    const tick = async () => {
      const pending = Object.values(jobsRef.current).filter(isPending);

      await Promise.all(
        pending.map(async (job) => {
          try {
            const { data: response } = await axios.get(
              `${QUERIES.ASYNC_QUERY_STATUS}/${encodeURIComponent(
                job.protocol,
              )}`,
            );
            const current = response?.data;
            if (!current) return;

            upsertJob(current);
            if (SETTLED.includes(current.jobStatus)) announce(current);
          } catch (error) {
            // Falha pontual de rede não encerra o acompanhamento: a próxima
            // rodada tenta de novo e o sino segue como canal de garantia.
            console.error("Falha ao consultar status da consulta:", error);
          }
        }),
      );
    };

    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasPending, announce, upsertJob]);

  const value = useMemo(
    () => ({ jobs, trackJob: upsertJob, hasPending }),
    [jobs, upsertJob, hasPending],
  );

  return (
    <AsyncQueryContext.Provider value={value}>
      {children}
    </AsyncQueryContext.Provider>
  );
};

export const useAsyncQueries = () => {
  const context = useContext(AsyncQueryContext);
  if (!context) {
    throw new Error(
      "useAsyncQueries precisa estar dentro de AsyncQueryProvider",
    );
  }
  return context;
};

export default AsyncQueryContext;
